"use client";

import { useRef, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn, dateFormater, formatRupee } from "@/lib/utils";
import { MapPin, Minus, Plus, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { type RouterOutputs } from "@/trpc/react";
import DateGroupSliderBooking from "../global/group-date-booking";
import { type UserType } from "@/types/user.type";
import PhoneLoginForm from "../auth/phone-login-form";
import { useRouter } from "next/navigation";
import { api } from "@/trpc/react";
import { Coupen } from "../icons/coupen";

interface AddonType {
    title: string;
    price: string;
    note?: string;
}

interface ModernBookingFormProps {
    data: RouterOutputs["trips"]["get_for_booking"];
    tripId: string;
    img?: string | null;
    user?: UserType;
}

export default function ModernBookingForm({
    data,
    tripId,
    // img,
    user,
}: ModernBookingFormProps) {
    const {
        addons,
        enabledPartialPay,
        // number_of_days,
        // number_of_nights,
        title,
        trip_batches,
        adentureType,
    } = data!;

    const router = useRouter()

    // State variables
    const [currentForm, setCurrentForm] = useState<number>(0);
    const [selectedCity, setSelectedCity] = useState<string>(
        trip_batches[0]?.city ?? "",
    );
    const [selectedLocation, setSelectedLocation] = useState<string | undefined>(
        undefined,
    );
    const [selectedDate, setSelectedDate] = useState<{
        id: string;
        start_date: Date;
        end_date: Date;
    } | null>(null);
    const [selectedDatePrice, setSelectedDatePrice] = useState<number | null>(
        null,
    );
    const [selectedTransportOptions, setSelectedTransportOptions] = useState<
        {
            id: string;
            transport: string;
            price_per_person: number;
            people: number;
            max_capacity: number;
        }[]
    >([]);
    const [selectedAddons, setSelectedAddons] = useState<Record<string, number>>(
        {},
    );
    const [isChecked, setIsChecked] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [isBookingInProgress, setIsBookingInProgress] = useState(false);
    const [discount, setDiscount] = useState<{
        discount_amount: number;
        couponId: string;
    } | null>();
    const [couponCode, setCouponCode] = useState<string>("");
    const paymentEnabled = trip_batches
        .find((batch) => batch.city === selectedCity)
        ?.available_dates.find((date) => date.id === selectedDate?.id)
        ?.enabled_booking !== false;
    const paymentEnabledRef = useRef(paymentEnabled);
    paymentEnabledRef.current = paymentEnabled;

    const createBookingMutation = api.booking.createBookingWithoutPayment.useMutation({
        onSuccess: (data) => {
            const canPay = paymentEnabledRef.current;
            toast.success(canPay ? "Booking created successfully!" : "Booking request saved.");
            setCurrentForm(3);
            if (canPay) {
                setTimeout(() => {
                    router.replace(`/payment/` + data.bookingId);
                }, 2000);
            }
        },
        onError: (error) => {
            toast.error(error.message ?? "Failed to create booking");
            setIsBookingInProgress(false);
        },
    });

    const { mutateAsync: fetchCoupon, isPending: couponLoading } =
        api.coupon.fetchCoupon.useMutation({
            onSuccess: (data) => {
                toast.success("Coupon applied successfully.");
                setDiscount(data);
            },
            onError: (error) => {
                toast.error(
                    error.message ?? "An error occurred while fetching coupon.",
                );
            },
        });

    // Handle city change
    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newCity = event.target.value;
        setSelectedCity(newCity);
        setSelectedDate(null);
        setSelectedDatePrice(null);
        setSelectedLocation(undefined);
        setSelectedTransportOptions([]);
        setSelectedAddons({});
    };

    // Handle date change
    const handleDateChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        dateData: { id: string; start_date: Date; end_date: Date; price: number },
    ) => {
        setSelectedDate({
            id: dateData.id,
            start_date: dateData.start_date,
            end_date: dateData.end_date,
        });
        setSelectedDatePrice(dateData.price);
        setSelectedTransportOptions([]);
        setSelectedAddons({});
    };

    // Get addons for the current selected date, or fallback to trip-level addons
    const getCurrentAddons = (): AddonType[] => {
        if (selectedDate) {
            const batch = trip_batches.find((b) => b.city === selectedCity);
            const selectedDateData = batch?.available_dates.find(
                (d) => d.id === selectedDate.id,
            );
            // Check if selected date has addons
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dateAddons = (selectedDateData as any)?.addons;
            if (dateAddons && Array.isArray(dateAddons) && dateAddons.length > 0) {
                return JSON.parse(JSON.stringify(dateAddons)) as AddonType[];
            }
        }
        // Fallback to trip-level addons
        return addons && Array.isArray(addons) && addons.length > 0
            ? (JSON.parse(JSON.stringify(addons)) as AddonType[])
            : [];
    };

    const currentAddons = getCurrentAddons();

    // Update people count for transport options
    const handleTransportPeopleChange = (
        action: "increment" | "decrement",
        transport: {
            id: string;
            transport: string;
            price_per_person: number;
            max_capacity: number;
        },
    ) => {
        setSelectedTransportOptions((prevOptions) => {
            const existingOption = prevOptions.find(
                (option) => option.id === transport.id,
            );

            let newPeopleCount = existingOption?.people ?? 0;
            if (action === "increment") {
                newPeopleCount++;
            } else if (action === "decrement" && newPeopleCount > 0) {
                newPeopleCount--;
            }

            if (newPeopleCount < 0 || newPeopleCount > transport.max_capacity) {
                return prevOptions;
            }

            // Get available seats for the selected date
            const batch = trip_batches.find((b) => b.city === selectedCity);
            const selectedDateData = batch?.available_dates.find(
                (d) => d.id === selectedDate?.id,
            );
            const availableSeats = selectedDateData?.available_seats ?? 0;

            // Calculate total people across all transport options
            const totalPeople = prevOptions.reduce((sum, opt) => {
                if (opt.id === transport.id) return sum + newPeopleCount;
                return sum + opt.people;
            }, 0);

            // Check if adding this person exceeds available seats
            if (totalPeople > availableSeats) {
                toast.error(`Cannot exceed available seats (${availableSeats})`);
                return prevOptions;
            }

            if (existingOption) {
                return prevOptions.map((option) =>
                    option.id === transport.id
                        ? { ...option, people: newPeopleCount }
                        : option,
                );
            } else {
                return [...prevOptions, { ...transport, people: newPeopleCount }];
            }
        });
    };

    // Update count for add-ons
    const handleAddonChange = (
        action: "increment" | "decrement",
        addon: AddonType,
    ) => {
        setSelectedAddons((prevAddons) => {
            const currentCount = prevAddons[addon.title] ?? 0;

            if (action === "increment") {
                // Check if new count would exceed total members
                if (currentCount + 1 > totalPeopleForTransport) {
                    toast.error(`Add-ons cannot exceed total members (${totalPeopleForTransport})`);
                    return prevAddons;
                }
                return { ...prevAddons, [addon.title]: currentCount + 1 };
            } else if (currentCount > 0) {
                return { ...prevAddons, [addon.title]: currentCount - 1 };
            }
            return prevAddons;
        });
    };

    // Get available dates for the selected city
    const getAvailableDates = () => {
        const selectedBatch = trip_batches.find(
            (batch) => batch.city === selectedCity,
        );
        return selectedBatch ? selectedBatch.available_dates : [];
    };

    const availableDates = getAvailableDates();

    // Get available seats for selected date
    const getAvailableSeats = () => {
        const batch = trip_batches.find((b) => b.city === selectedCity);
        const selectedDateData = batch?.available_dates.find(
            (d) => d.id === selectedDate?.id,
        );
        return selectedDateData?.available_seats ?? 0;
    };

    const availableSeats = getAvailableSeats();

    // Calculate the total number of people from transport options
    const totalPeopleForTransport = selectedTransportOptions.reduce(
        (sum, option) => sum + option.people,
        0,
    );

    // Calculate the total price
    const transportCost = selectedTransportOptions.reduce(
        (sum, option) => sum + option.price_per_person * option.people,
        0,
    );
    const dateCost = selectedDatePrice
        ? selectedDatePrice * totalPeopleForTransport
        : 0;
    const addonCost = Object.keys(selectedAddons).reduce((sum, key) => {
        const addon: AddonType | undefined = currentAddons.find((a: AddonType) => a.title === key);
        return (
            sum + (addon ? parseInt(addon.price) * (selectedAddons[key] ?? 0) : 0)
        );
    }, 0);

    const totalPrice = transportCost + dateCost + addonCost;
    const subtotalAfterDiscount = totalPrice - (discount?.discount_amount ?? 0);
    const gst_amount = Number((subtotalAfterDiscount * 0.05).toFixed(2));
    const grandTotal = Number((subtotalAfterDiscount + gst_amount).toFixed(2));

    const quoteTransport = selectedTransportOptions
        .filter((option) => option.people > 0)
        .map((option) => ({
            transport: option.transport,
            numberOfTravelers: option.people,
        }));
    const quoteAddons = Object.entries(selectedAddons)
        .filter(([, count]) => count > 0)
        .map(([addonTitle, count]) => ({
            addon_title: addonTitle,
            addon_unit: count,
        }));
    const canQuote = Boolean(
        selectedDate &&
        selectedCity &&
        totalPeopleForTransport > 0 &&
        quoteTransport.length > 0,
    );
    const { data: paymentQuote } = api.booking.paymentQuote.useQuery(
        {
            tripId,
            adventureType: adentureType,
            city: selectedCity,
            startDate: selectedDate?.start_date ?? new Date(0),
            endDate: selectedDate?.end_date ?? new Date(0),
            totalMembers: Math.max(totalPeopleForTransport, 1),
            transportDetails: quoteTransport,
            addonDetails: quoteAddons,
            discountAmount: discount?.discount_amount ?? 0,
        },
        { enabled: canQuote },
    );

    const handleCouponFetch = async () => {
        if (!couponCode.trim()) {
            toast.error("Please enter a coupon code.");
            return;
        }
        await fetchCoupon({
            code: couponCode,
            productId: tripId,
            totalAmount: totalPrice,
            userId: user?.id,
        });
    };

    // Handle booking submission
    const handleBooking = () => {
        // Validate all required fields
        if (!selectedCity) {
            toast.error("Please select a city.");
            return;
        }

        if (!selectedDate) {
            toast.error("Please select a date for your booking.");
            return;
        }

        if (!selectedDatePrice) {
            toast.error("Unable to calculate price. Please try selecting the date again.");
            return;
        }

        if (totalPeopleForTransport <= 0) {
            toast.error("Please select at least one person for booking.");
            return;
        }

        if (selectedTransportOptions.length === 0 || selectedTransportOptions.every(opt => opt.people === 0)) {
            toast.error("Please select transport options.");
            return;
        }

        // Check if user is logged in
        if (!user) {
            setShowLoginPopup(true);
            return;
        }

        completeBooking();
    };

    const completeBooking = () => {
        if (isBookingInProgress) return;

        setIsBookingInProgress(true);

        const addonDetails = Object.entries(selectedAddons)
            .filter(([, count]) => count > 0)
            .map(([addonTitle, count]) => ({
                addon_title: addonTitle,
                addon_price: currentAddons.find((a) => a.title === addonTitle)?.price ?? "0",
                addon_unit: count,
            }));

        const bookingData = {
            tripId,
            tripTitle: title,
            adventureType: adentureType,
            city: selectedCity,
            pickupLocation: selectedLocation,
            startDate: selectedDate!.start_date,
            endDate: selectedDate!.end_date,
            totalMembers: totalPeopleForTransport,
            transportDetails: selectedTransportOptions
                .filter((option) => option.people > 0)
                .map((option) => ({
                    transport: option.transport,
                    numberOfTravelers: option.people,
                    pricePerPerson: selectedDatePrice! + option.price_per_person,
                    totalPrice: (selectedDatePrice! + option.price_per_person) * option.people,
                })),
            addonDetails,
            pricing: {
                basePrice: dateCost,
                transportCost,
                addonCost,
                subtotal: totalPrice,
                gst: gst_amount,
                grandTotal,
                enabledPartialPay,
                partialAmount: paymentQuote?.partialAvailable ? paymentQuote.payNow : null,
                remainingAmount: paymentQuote?.partialAvailable ? paymentQuote.payLater : null,
            },
            phone: user?.phone ?? "",
            couponId: discount?.couponId,
            discountAmount: discount?.discount_amount,
        };

        createBookingMutation.mutate(bookingData);
    };

    const handleLoginSuccess = () => {
        setShowLoginPopup(false);
        toast.success("Login successful! Completing your booking...");
        // Small delay to allow toast to show and page to re-render with user data
        setTimeout(() => {
            completeBooking();
        }, 500);
    };

    const steps = [
        { label: "Select Batch", value: 0 },
        { label: "No. Of Members", value: 1 },
        { label: "Review & Confirm", value: 2 },
        { label: "Booking Status", value: 3 },
    ];

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked);
    };
    return (
        <div className="mx-auto max-w-6xl">
            {/* Login Popup */}
            {showLoginPopup && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <button
                            onClick={() => setShowLoginPopup(false)}
                            className="absolute right-4 top-4 text-gray-400 transition-colors hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                        <PhoneLoginForm onLoginSuccess={handleLoginSuccess} />
                    </div>
                </div>
            )}

            {/* Progress Steps */}
            <div className="mb-8 grid grid-cols-4 gap-2.5 md:gap-3">
                {steps.map((step, index) => (
                    <div key={step.value} className="flex flex-col justify-end gap-2">
                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-center gap-2">
                                <div
                                    className={cn(
                                        "flex size-7 md:size-8 items-center justify-center rounded-full border text-sm font-semibold transition-all",
                                        currentForm >= step.value
                                            ? "border-custom-primary bg-custom-primary text-white"
                                            : "border-gray-300 bg-white text-gray-400",
                                    )}
                                >
                                    {currentForm > step.value ? (
                                        <Check className="h-5 w-5" />
                                    ) : (
                                        index + 1
                                    )}
                                </div>
                            </div>
                            <p
                                className={cn(
                                    "hidden sm:block text-xs font-medium leading-tight transition-colors md:text-sm",
                                    currentForm >= step.value ? "text-custom-primary" : "text-gray-400",
                                )}
                            >
                                {step.label}
                            </p>
                        </div>
                        <span className="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                            <span
                                className={cn(
                                    "absolute left-0 top-0 h-full w-full origin-left bg-custom-primary transition-transform duration-500",
                                    currentForm >= step.value ? "scale-x-100" : "scale-x-0",
                                )}
                            />
                        </span>
                    </div>
                ))}
            </div>

            {/* Main Card */}
            <Card className="overflow-hidden border border-primary-text/25 shadow-none sm:rounded-2xl">
                <CardHeader className="border-b border-primary-text/25 bg-neutral-100 p-4 text-center">
                    <div
                        className="flex h-full w-full flex-row flex-nowrap transition-all duration-500 text-center text-lg sm:text-xl lg:text-2xl font-medium sm:font-semibold text-primary-text"
                        style={{ transform: `translateX(-${currentForm * 100}%)` }}
                    >
                        <h2 className="w-full shrink-0">
                            Select Boarding City and Date
                        </h2>
                        <h2 className="w-full shrink-0">
                            Select Number Of Members
                        </h2>
                        <h2 className="w-full shrink-0">
                            Review & Confirm Booking
                        </h2>
                        <h2 className="w-full shrink-0">
                            Booking Completed Successfully
                        </h2>
                    </div>
                </CardHeader>
                <CardContent className="w-full overflow-hidden p-0">
                    <div
                        className="flex h-full w-full flex-row flex-nowrap transition-all duration-500"
                        style={{ transform: `translateX(-${currentForm * 100}%)` }}
                    >
                        {/* Step 1: City and Date Selection */}
                        <div className="w-full shrink-0 space-y-6 p-6 md:p-8">
                            {/* City Selection */}
                            <div className="space-y-3">
                                <h3 className="text-base md:text-lg font-medium text-primary-text">
                                    Select Boarding City:
                                </h3>
                                <div className="flex flex-row flex-wrap gap-3">
                                    {trip_batches.map((batch) => (
                                        <div key={batch.city} className="flex w-fit">
                                            <input
                                                type="radio"
                                                id={batch.city}
                                                value={batch.city}
                                                checked={selectedCity === batch.city}
                                                onChange={handleCityChange}
                                                className="peer sr-only"
                                            />
                                            <label
                                                htmlFor={batch.city}
                                                className="flex w-fit cursor-pointer items-center gap-1 md:gap-1.5 rounded-lg border-[1.5px] border-gray-300 bg-white px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm md:text-base text-primary-text transition-all hover:border-custom-primary hover:bg-custom-primary-shade peer-checked:border-custom-primary peer-checked:bg-custom-primary peer-checked:text-white"
                                            >
                                                <MapPin className="size-3.5 md:size-4 shrink-0" strokeWidth={2} />
                                                {batch.city}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pickup Location */}
                            <div className="space-y-3">
                                <h3 className="text-base md:text-lg font-medium text-primary-text">
                                    Select Pickup Location:
                                </h3>
                                <div className="flex flex-row flex-wrap gap-3">
                                    {trip_batches.find((b) => b.city === selectedCity)
                                        ?.pickup_location ? (
                                        trip_batches
                                            .find((b) => b.city === selectedCity)
                                            ?.pickup_location?.split(", ")
                                            .map((location) => (
                                                <div key={location} className="flex w-fit">
                                                    <input
                                                        type="radio"
                                                        id={location}
                                                        value={location}
                                                        checked={selectedLocation === location}
                                                        onChange={(e) =>
                                                            setSelectedLocation(e.target.value)
                                                        }
                                                        className="peer sr-only"
                                                    />
                                                    <label
                                                        htmlFor={location}
                                                        className="flex w-fit cursor-pointer items-center gap-1 md:gap-1.5 rounded-lg border-[1.5px] border-gray-300 bg-white px-2.5 py-1.5 sm:px-3 sm:py-2 text-sm md:text-base text-primary-text transition-all hover:border-custom-primary hover:bg-custom-primary-shade peer-checked:border-custom-primary peer-checked:bg-custom-primary peer-checked:text-white"
                                                    >
                                                        <MapPin className="size-3.5 md:size-4 shrink-0" strokeWidth={2} />
                                                        {location}
                                                    </label>
                                                </div>
                                            ))
                                    ) : (
                                        <p className="text-gray-500">
                                            No Pickup location available.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Date Selection */}
                            <div className="w-full space-y-3">
                                <h3 className="text-base md:text-lg font-medium text-primary-text">
                                    Available Dates for {selectedCity}:
                                </h3>
                                <DateGroupSliderBooking
                                    availableDates={availableDates}
                                    selectedDate={selectedDate}
                                    setSelectedDate={setSelectedDate}
                                    handleDateChange={handleDateChange}
                                />
                            </div>

                            <div className="flex justify-end pt-2.5">
                                <button
                                    disabled={
                                        selectedDate === null || selectedLocation === undefined
                                    }
                                    onClick={() => setCurrentForm(currentForm + 1)}
                                    className="rounded-lg bg-custom-primary px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base text-white transition-all hover:bg-custom-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Continue to Members
                                </button>
                            </div>
                        </div>

                        {/* Step 2: Members and Transport */}
                        <div className="w-full shrink-0 space-y-6 p-6 md:p-8">
                            {/* Transport Options */}
                            <div className="space-y-4">
                                <h3 className="font-lexend text-base md:text-lg font-medium text-primary-text">
                                    Transport Options
                                </h3>
                                {(() => {
                                    const batch = trip_batches.find(
                                        (batch) => batch.city === selectedCity,
                                    );
                                    if (!batch) {
                                        return (
                                            <p className="text-center text-gray-600">
                                                No transport options available.
                                            </p>
                                        );
                                    }

                                    const selectedDateData = batch.available_dates.find(
                                        (d) => d.id === selectedDate?.id,
                                    );
                                    if (
                                        !selectedDateData ||
                                        selectedDateData.mode_of_transports.length === 0
                                    ) {
                                        return (
                                            <p className="text-center text-gray-600">
                                                No transport options available for selected date.
                                            </p>
                                        );
                                    }

                                    return selectedDateData.mode_of_transports.map((option) => {
                                        const selectedOption = selectedTransportOptions.find(
                                            (op) => op.id === option.id,
                                        );
                                        const isMaxCapacityReached = selectedOption && selectedOption.people >= option.max_capacity;
                                        const isSeatsLimitReached = totalPeopleForTransport >= availableSeats;

                                        return (
                                            <div
                                                key={option.id}
                                                className="flex items-center justify-between rounded-lg border border-gray-300 bg-white p-2.5 transition-all hover:border-custom-primary hover:shadow"
                                            >
                                                <div className="flex-1">
                                                    <p className="font-medium text-primary-text text-sm sm:text-base">
                                                        {option.transport}
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-primary-text/55">
                                                        Max capacity: {option.max_capacity} people
                                                    </p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-custom-primary">
                                                        ₹{selectedDatePrice ? selectedDatePrice + option.price_per_person : option.price_per_person}
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-primary-text/55">per person</p>
                                                </div>
                                                <div className="ml-4 sm:ml-24 lg:ml-36 flex items-center gap-1 sm:gap-2.5">
                                                    <button
                                                        onClick={() =>
                                                            handleTransportPeopleChange("decrement", option)
                                                        }
                                                        disabled={!selectedOption || selectedOption.people === 0}
                                                        className="flex size-5 sm:size-6 p-0.5 items-center justify-center rounded-full border border-primary-text/45 bg-white text-gray-600 transition-all hover:border-custom-primary hover:bg-custom-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        <Minus className="size-full" />
                                                    </button>
                                                    <span className="w-6 sm:w-8 text-center font-bold text-gray-800">
                                                        {selectedOption?.people ?? 0}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            handleTransportPeopleChange("increment", option)
                                                        }
                                                        disabled={isMaxCapacityReached ?? isSeatsLimitReached}
                                                        className="flex size-5 sm:size-6 p-0.5 items-center justify-center rounded-full border border-primary-text/45 bg-white text-gray-600 transition-all hover:border-custom-primary hover:bg-custom-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        <Plus className="size-full" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            {/* Add-ons */}
                            <div className="space-y-4">
                                <h3 className="text-base md:text-lg font-medium text-primary-text">
                                    Add-On Options
                                </h3>
                                {currentAddons.length > 0 ? (
                                    currentAddons.map((addon, index) => {
                                        const isMaxReached = (selectedAddons[addon.title] ?? 0) >= totalPeopleForTransport;

                                        return (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between rounded-lg border border-gray-300 bg-white p-2.5 transition-all hover:border-custom-primary hover:shadow"
                                            >
                                                <div className="flex-1">
                                                    <p className="font-medium text-primary-text text-sm sm:text-base">
                                                        {addon.title}
                                                    </p>
                                                    {addon?.note && (
                                                        <p className="text-[10px] sm:text-xs text-primary-text/55">
                                                            {addon.note}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-custom-primary">
                                                        ₹{addon.price}
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-primary-text/55">per person</p>
                                                </div>
                                                <div className="ml-4 sm:ml-24 lg:ml-36 flex flex-col items-center">
                                                    <div className="flex items-center gap-1 sm:gap-2.5">
                                                        <button
                                                            onClick={() => handleAddonChange("decrement", addon)}
                                                            disabled={!selectedAddons[addon.title] || selectedAddons[addon.title] === 0}
                                                            className="flex size-5 sm:size-6 p-0.5 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition-all hover:border-custom-primary hover:bg-custom-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            <Minus className="size-full" />
                                                        </button>
                                                        <span className="w-6 sm:w-8 text-center font-bold text-gray-800">
                                                            {selectedAddons[addon.title] ?? 0}
                                                        </span>
                                                        <button
                                                            onClick={() => handleAddonChange("increment", addon)}
                                                            disabled={isMaxReached || totalPeopleForTransport === 0}
                                                            className="flex size-5 sm:size-6 p-0.5 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition-all hover:border-custom-primary hover:bg-custom-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            <Plus className="size-full" />
                                                        </button>
                                                    </div>
                                                    {/* {totalPeopleForTransport > 0 && (
                                                        <p className="text-[10px] sm:text-xs text-primary-text/55">
                                                            Max: {totalPeopleForTransport}
                                                        </p>
                                                    )} */}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-center text-gray-500">
                                        No add-on options available.
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center justify-between pt-4">
                                <button
                                    onClick={() => setCurrentForm(currentForm - 1)}
                                    className="rounded-lg border-[1.5px] border-gray-300 bg-white px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50"
                                >
                                    Back to Batch
                                </button>
                                <button
                                    disabled={totalPeopleForTransport <= 0}
                                    onClick={() => setCurrentForm(currentForm + 1)}
                                    className="rounded-lg bg-custom-primary px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base text-white transition-all hover:bg-custom-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Review Booking
                                </button>
                            </div>
                        </div>

                        {/* Step 3: Review and Confirm */}
                        <div className="w-full shrink-0 space-y-6 p-6 md:p-8">
                            <div className="grid gap-6 lg:grid-cols-3">
                                {/* Trip Details */}
                                <div className="space-y-6 lg:col-span-2">
                                    <div className="rounded-xl border border-primary-text/25 bg-white p-4 lg:p-6">
                                        <h3 className="mb-4 text-base sm:text-lg font-semibold text-primary-text">
                                            Trip Details
                                        </h3>
                                        <div className="space-y-3 text-xs sm:text-sm">
                                            <div className="flex justify-between border-b border-gray-200 pb-2">
                                                <span className="text-gray-600">Route:</span>
                                                <span className="font-semibold text-gray-800">
                                                    {selectedCity} to {selectedCity}
                                                </span>
                                            </div>
                                            <div className="flex justify-between border-b border-gray-200 pb-2">
                                                <span className="text-gray-600">Pickup Location:</span>
                                                <span className="font-semibold text-gray-800">
                                                    {selectedLocation}
                                                </span>
                                            </div>
                                            <div className="flex justify-between border-b border-gray-200 pb-2">
                                                <span className="text-gray-600">Trip Date:</span>
                                                <span className="font-semibold text-gray-800">
                                                    {selectedDate?.start_date &&
                                                        dateFormater({
                                                            date: selectedDate.start_date,
                                                            formatString: "dd MMM yyyy",
                                                        })}{" "}
                                                    to{" "}
                                                    {selectedDate?.end_date &&
                                                        dateFormater({
                                                            date: selectedDate.end_date,
                                                            formatString: "dd MMM yyyy",
                                                        })}
                                                </span>
                                            </div>
                                            <div className="flex justify-between border-b border-gray-200 pb-2">
                                                <span className="text-gray-600">Total Members:</span>
                                                <span className="font-semibold text-gray-800">
                                                    {totalPeopleForTransport} Members
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Transport Details */}
                                    <div className="rounded-xl border border-primary-text/25 bg-white p-4 lg:p-6">
                                        <h3 className="mb-2 text-base sm:text-lg font-semibold text-primary-text">
                                            Transport Selection
                                        </h3>
                                        <div className="space-y-3">
                                            {selectedTransportOptions.length > 0 ? (
                                                selectedTransportOptions.map((option) => {
                                                    if (option.people > 0)
                                                        return (
                                                            <div
                                                                key={option.id}
                                                                className="flex items-center justify-between rounded-lg bg-neutral-100/75 p-2.5 sm:p-4"
                                                            >
                                                                <div>
                                                                    <p className="text-sm sm:text-base font-medium text-primary-text">
                                                                        {option.transport}
                                                                    </p>
                                                                    <p className="text-xs sm:text-sm text-gray-500">
                                                                        {option.people} person(s) × ₹
                                                                        {selectedDatePrice! + option.price_per_person}
                                                                    </p>
                                                                </div>
                                                                <p className="font-bold text-custom-primary">
                                                                    ₹
                                                                    {(selectedDatePrice! + option.price_per_person) *
                                                                        option.people}
                                                                </p>
                                                            </div>
                                                        );
                                                })
                                            ) : (
                                                <p className="text-gray-500">No transport selected.</p>
                                            )}
                                        </div>

                                        {/* Add-ons Details */}
                                        {Object.entries(selectedAddons).some(([, count]) => count > 0) && (
                                            <div className="border-t border-gray-200 mt-5">
                                                <h3 className="mt-3 mb-2 text-base sm:text-lg font-semibold text-primary-text">
                                                    Add-Ons Selection
                                                </h3>
                                                <div className="space-y-3">
                                                    {currentAddons.map((addon) => {
                                                        if ((selectedAddons?.[addon.title] ?? 0) > 0)
                                                            return (
                                                                <div
                                                                    key={addon.title}
                                                                    className="flex items-center justify-between rounded-lg bg-neutral-100/75 p-2.5 sm:p-4"
                                                                >
                                                                    <div>
                                                                        <p className="text-sm sm:text-base font-medium text-primary-text">
                                                                            {addon.title}
                                                                        </p>
                                                                        <p className="text-xs sm:text-sm text-gray-500">
                                                                            {selectedAddons[addon.title]} unit(s) × ₹
                                                                            {addon.price}
                                                                        </p>
                                                                    </div>
                                                                    <p className="font-bold text-custom-primary">
                                                                        ₹
                                                                        {parseInt(addon.price) *
                                                                            (selectedAddons[addon.title] ?? 0)}
                                                                    </p>
                                                                </div>
                                                            );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                </div>

                                {/* Price Summary */}
                                <div className="space-y-6 lg:col-span-1 sticky top-6">
                                    {/* Coupon Section */}
                                    {/* <div className="rounded-xl border border-primary-text/25 bg-white p-4 lg:p-6">
                                        <h3 className="mb-3 text-lg font-semibold text-gray-800">
                                            Have a coupon?
                                        </h3>
                                    </div> */}

                                    <div className="rounded-xl bg-neutral-100/75 p-4 lg:p-6">
                                        <h3 className="mb-4 text-base sm:text-lg font-semibold text-primary-text">
                                            Price Summary
                                        </h3>
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Base Price:</span>
                                                <span className="font-semibold text-gray-800">
                                                    ₹{dateCost + transportCost}
                                                </span>
                                            </div>
                                            {/* <div className="flex justify-between">
                                                <span className="text-gray-600">Transport Cost:</span>
                                                <span className="font-semibold text-gray-800">
                                                    ₹{transportCost}
                                                </span>
                                            </div> */}
                                            {addonCost > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Add-ons Cost:</span>
                                                    <span className="font-semibold text-gray-800">
                                                        ₹{addonCost}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="border-t border-gray-300 pt-3">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Base Amount:</span>
                                                    <span className="font-semibold text-gray-800">
                                                        ₹{totalPrice}
                                                    </span>
                                                </div>
                                            </div>
                                            {discount && (
                                                <>
                                                    <div className="flex justify-between">
                                                        <span className="text-green-600">Discount:</span>
                                                        <span className="font-semibold text-green-600">
                                                            -₹{discount.discount_amount}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Subtotal:</span>
                                                        <span className="font-semibold text-gray-800">
                                                            ₹{subtotalAfterDiscount}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">GST (5%):</span>
                                                <span className="font-semibold text-gray-800">
                                                    ₹{gst_amount}
                                                </span>
                                            </div>
                                            <div className="border-t border-gray-300 pt-3">
                                                <div className="flex justify-between text-base sm:text-lg">
                                                    <span className="font-semibold text-primary">
                                                        Grand Total:
                                                    </span>
                                                    <span className="font-bold text-custom-primary">
                                                        ₹{grandTotal}
                                                    </span>
                                                </div>
                                            </div>
                                            {paymentEnabled && paymentQuote?.partialAvailable && (
                                                <>
                                                    <div className="flex justify-between rounded-lg border border-custom-primary/25 bg-custom-primary/10 px-2.5 py-1.5 text-sm">
                                                        <span className="font-medium text-primary-text">
                                                            Partial Payment:
                                                        </span>
                                                        <span className="font-bold text-custom-primary">
                                                            ₹{formatRupee(paymentQuote.payNow)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-600">
                                                            Remaining:
                                                        </span>
                                                        <span className="font-semibold text-gray-800">
                                                            ₹{formatRupee(paymentQuote.payLater)}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-2.5 lg:p-4 bg-neutral-100/75 rounded-xl">
                                        <h3 className="mb-4 text-xs sm:text-sm font-medium text-primary-text">
                                            Have a coupon? Apply it here.
                                        </h3>
                                        <div className="relative flex gap-2">
                                            <div className="relative flex-1">
                                                <input
                                                    type="text"
                                                    className="h-10 w-full rounded-lg border border-gray-300 pl-8 pr-1 text-sm focus:border-custom-primary focus:outline-none focus:ring-2 focus:ring-custom-primary/20"
                                                    placeholder="Enter coupon code"
                                                    value={couponCode}
                                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                    disabled={couponLoading || !!discount}
                                                />
                                                <Coupen className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                            </div>
                                            <button
                                                onClick={handleCouponFetch}
                                                disabled={couponLoading || !couponCode.trim() || !!discount}
                                                className="rounded-lg bg-custom-primary px-3 py-2 text-sm font-medium text-white transition-all hover:bg-custom-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {couponLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : discount ? (
                                                    "Applied"
                                                ) : (
                                                    "Apply"
                                                )}
                                            </button>
                                        </div>
                                        {discount && (
                                            <div className="mt-3 flex items-center justify-between rounded-lg bg-green-50 p-3">
                                                <div className="flex items-center gap-2">
                                                    <Check className="h-4 w-4 text-green-600" />
                                                    <span className="text-sm font-medium text-green-700">
                                                        Coupon applied successfully!
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setDiscount(null);
                                                        setCouponCode("");
                                                    }}
                                                    className="text-sm text-green-600 hover:text-green-700"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Terms and Conditions */}
                            <div className="rounded-xl border border-primary-text/25 bg-white p-2.5 lg:p-4">
                                <div className="flex items-start md:items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="terms-conditions"
                                        checked={isChecked}
                                        onChange={handleCheckboxChange}
                                        className="mt-1 md:mt-0 size-4 shrink-0 cursor-pointer rounded border-gray-300 text-custom-primary focus:ring-2 focus:ring-custom-primary"
                                    />
                                    <label
                                        htmlFor="terms-conditions"
                                        className="cursor-pointer text-sm text-gray-700"
                                    >
                                        I&apos;ve read and accept the{" "}
                                        <a
                                            href="/terms-and-conditions"
                                            target="_blank"
                                            className="font-semibold text-custom-primary underline hover:text-custom-primary/80"
                                        >
                                            Terms and Conditions
                                        </a>
                                        ,{" "}
                                        <a
                                            href="/cancellation-policy"
                                            target="_blank"
                                            className="font-semibold text-custom-primary underline hover:text-custom-primary/80"
                                        >
                                            Cancellation Policy
                                        </a>
                                        , and{" "}
                                        <a
                                            href="/payment-policy"
                                            target="_blank"
                                            className="font-semibold text-custom-primary underline hover:text-custom-primary/80"
                                        >
                                            Payment Policy
                                        </a>
                                        .
                                    </label>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <button
                                    onClick={() => setCurrentForm(currentForm - 1)}
                                    disabled={isBookingInProgress}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Back to Members
                                </button>
                                <button
                                    disabled={!isChecked || isBookingInProgress}
                                    onClick={handleBooking}
                                    className="rounded-lg bg-custom-primary px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base text-white transition-all hover:bg-custom-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isBookingInProgress ? "Creating Booking..." : "Continue"}
                                </button>
                            </div>

                            {!paymentEnabled && (
                                <div className="rounded-xl border-2 border-yellow-200 bg-yellow-50 p-4">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Note:</strong> Payment is not available for this departure.
                                        Your booking will be saved as an enquiry and our team will follow up.
                                    </p>
                                </div>
                            )}
                            {paymentEnabled && paymentQuote?.partialAvailable && (
                                <div className="rounded-xl border-2 border-yellow-200 bg-yellow-50 p-4">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Note:</strong> A partial payment is required at the time of booking.
                                        The remaining amount must be paid at least 5 days before the
                                        scheduled departure date.
                                    </p>
                                </div>
                            )}
                            {paymentEnabled && enabledPartialPay && paymentQuote && !paymentQuote.partialAvailable && (
                                <div className="rounded-xl border-2 border-yellow-200 bg-yellow-50 p-4">
                                    <p className="text-sm text-yellow-800">
                                        <strong>Note:</strong> Full payment is required because this departure is within the next 5 days.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Step 4: Success */}
                        <div className="flex w-full shrink-0 items-center justify-center p-6 md:p-8">
                            <div className="max-w-md space-y-6 text-center">
                                <div className="mx-auto flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-green-100">
                                    <Check className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
                                </div>
                                <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold sm:font-bold text-primary-text">
                                    Booking Created Successfully!
                                </h3>
                                <p className="text-sm sm:text-base text-primary-text/70">
                                    {paymentEnabled
                                        ? "Your booking has been created. You will receive a confirmation email after payment."
                                        : "Your booking request is saved. Our team will follow up with you."}
                                </p>
                                <div className="rounded-xl border border-primary-text/25 bg-neutral-100/75 p-4 lg:p-6 text-left">
                                    <h4 className="mb-3 text-base md:text-lg font-medium text-primary-text">
                                        Summary:
                                    </h4>
                                    <div className="space-y-2 text-xs sm:text-sm text-primary-text/70">
                                        <p>Trip: {title}</p>
                                        <p>City: {selectedCity}</p>
                                        <p>Members: {totalPeopleForTransport}</p>
                                        <p>Total Amount: ₹{grandTotal}</p>
                                        {user && (
                                            <>
                                                <p>Name: {user.fullname}</p>
                                                <p>Email: {user.email}</p>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={() => router.push('/user/bookings')}
                                    className="w-full rounded-lg bg-custom-primary px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-medium text-white transition-all hover:bg-custom-primary/90"
                                >
                                    View My Bookings
                                </button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
